import Vue from 'vue'
import isEqual from 'lodash/isEqual'
import contracts, { isLifeInsurance, feesList, feesSpecialList } from '@/api/contracts'
import realestates from '@/api/realestates'
import updateRecord from '@/utils/update-record'
import deleteRecord from '@/utils/delete-record'
import findIndex from 'lodash/findIndex'
import { ANALYSE_STEPS } from '@/api/analyse'

const getBaseState = () => ({
  aggregates: {},
  graphs: {},
  list: [],
  realEstates: [],
  targets: {},
  analyses: {},
  histories: {},
  transactions: {},
  transactionCategories: [],
  transactionsMovements: {},
  contractsRepartition: [],
  questions: null,
  optimizedPortfolio: {},
  arbitrationPortfolio: {},
  analysing: false,
  completeHistory: {},
  composition: {}
})

export default () => ({
  namespaced: true,

  state: getBaseState(),

  actions: {
    fetch (context, cached = true) {
      const { list } = context.state
      if (cached && list.length) {
        return Promise.resolve(list)
      }

      return contracts.fetch()
        .then(data => {
          context.commit('fetch', data)
        })
    },

    fetchById (context, { id, cached = true }) {
      let contract
      if (cached && (contract = context.getters.getById(id))) {
        return Promise.resolve(contract)
      }
      return contracts.fetchById(id)
        .then(contract => {
          context.commit('add', contract)
          return contract
        })
    },

    fetchCompleteHistory (context, { cached = true, subset = 'all' }) {
      if (cached && context.state.completeHistory[subset]) {
        return Promise.resolve()
      }
      return contracts.fetchCompleteHistory(subset)
        .then(data => context.commit('fetchCompleteHistory', { data, subset }))
    },

    fetchQuestions (context, cached = true) {
      const { questions } = context.state
      if (cached && questions) {
        return Promise.resolve(questions)
      }

      return contracts.fetchQuestions()
        .then(({ riskSurvey }) => context.commit('fetchQuestions', riskSurvey.filter(({type}) => type === 'contract')))
    },

    fetchTargets (context) {
      return contracts.fetchTargets()
        .then(data => context.commit('fetchTargets', data))
    },

    fetchAnalyses (context, { contractId, section, cached = true }) {
      if (cached && context.state.analyses[contractId] &&
        context.state.analyses[contractId][section]) {
        return Promise.resolve(context.state.analyses[contractId][section])
      }
      context.commit('analysing')
      return contracts.fetchAnalyses(contractId, section)
        .then(data => context.commit('fetchAnalyses', { contractId, section, data }))
    },

    fetchHistory (context, { contractId, from, cached = true }) {
      if (cached && context.state.histories[contractId]) {
        return Promise.resolve(context.state.histories[contractId])
      }
      return contracts.fetchHistory(contractId, from)
        .then(history => {
          context.commit('fetchHistory', { contractId, history })
          return history
        })
    },

    fetchOptimize (context, { contractId, cached = true }) {
      if (cached && context.state.optimizedPortfolio[contractId]) {
        return Promise.resolve()
      }
      context.commit('analysing')
      return contracts.fetchOptimize(contractId)
        .then(data => {
          return Promise.all(
            ANALYSE_STEPS
              .map(({name}) => name)
              .slice(0, -1)
              .map(step => context.dispatch('fetchAnalyses', { contractId: data.id, section: step }))
          )
            .then(() => context.commit('fetchOptimize', { contractId, data }))
        })
    },

    fetchArbitration (context, { contractId, method, metric, funds, cached = false }) {
      const fullMethod = `${method}${metric ? `-${metric}` : ''}`
      if (cached && context.state.arbitrationPortfolio[contractId] &&
        context.state.arbitrationPortfolio[contractId][fullMethod]) {
        return Promise.resolve()
      }
      return contracts.fetchOptimize(contractId, { strategy: method, metric, investments: funds })
        .then(data => {
          return Promise.all(
            ANALYSE_STEPS
              .map(({name}) => name)
              .slice(0, -1)
              .map(step => context.dispatch('fetchAnalyses', { contractId: data.id, section: step }))
          )
            .then(() => context.commit('fetchArbitration', { contractId, method: fullMethod, data }))
        })
    },

    fetchByAccount (context, id) {
      return contracts.fetchByAccount(id)
        .then(data => context.commit('fetchSlice', data))
    },

    fetchRepartition (context, cached) {
      if (cached && context.state.contractsRepartition.length) {
        return Promise.resolve()
      }
      return contracts.fetchRepartition()
        .then(({ data }) => context.commit('fetchRepartition', data))
    },

    fetchAggregateContractRepartition (context, cached) {
      if (cached && context.state.contractsRepartition.length) {
        return Promise.resolve()
      }
      return contracts.fetchAggregateContractRepartition()
        .then(({ aggregates }) => {
          context.commit('saveAgreggate', aggregates[0])
        })
    },

    fetchAggregateRealestateType (context, cached) {
      if (cached && context.state.contractsRepartition.length) {
        return Promise.resolve()
      }
      return contracts.fetchAggregateRealestateType()
        .then(({ aggregates }) => {
          context.commit('saveAgreggate', aggregates[0])
        })
    },

    fetchAggregateRealestateEnv (context, cached) {
      if (cached && context.state.contractsRepartition.length) {
        return Promise.resolve()
      }
      return contracts.fetchAggregateRealestateEnv()
        .then(({ aggregates }) => {
          context.commit('saveAgreggate', aggregates[0])
        })
    },

    fetchEuroUCComposition (context, { subset, limit = null, cached = true }) {
      if (cached && context.state.composition[subset] && context.state.composition[subset]['risk'] &&
        context.state.composition[subset]['risk'].metadata.limit === limit) {
        return Promise.resolve()
      }
      return contracts.fetchComposition(subset, 'risk', limit)
        .then(data => context.commit('fetchComposition', { data, subset, type: 'risk' }))
    },

    fetchKindComposition (context, { subset, limit = null, cached = true }) {
      if (cached && context.state.composition[subset] && context.state.composition[subset]['kind'] &&
        context.state.composition[subset]['kind'].metadata.limit === limit) {
        return Promise.resolve()
      }
      return contracts.fetchComposition(subset, 'kind', limit)
        .then(data => context.commit('fetchComposition', { data, subset, type: 'kind' }))
    },

    fetchSpendingComposition (context, { subset, limit = null, cached = true }) {
      if (cached && context.state.composition[subset] && context.state.composition[subset]['spending'] &&
        context.state.composition[subset]['spending'].metadata.limit === limit) {
        return Promise.resolve()
      }
      return contracts.fetchComposition(subset, 'spending', limit)
        .then(data => context.commit('fetchComposition', { data, subset, type: 'spending' }))
    },

    fetchAssetClassComposition (context, { subset, limit = null, cached = true }) {
      if (cached && context.state.composition[subset] && context.state.composition[subset]['asset_class'] &&
        context.state.composition[subset]['asset_class'].metadata.limit === limit) {
        return Promise.resolve()
      }
      return contracts.fetchComposition(subset, 'asset_class', limit)
        .then(data => context.commit('fetchComposition', { data, subset, type: 'asset_class' }))
    },

    fetchLoansComposition (context, cached) {
      if (cached && context.state.contractsRepartition.length) {
        return Promise.resolve()
      }
      return contracts.fetchComposition('loan', 'kind')
        .then(({ data }) => context.commit('fetchRepartition', data))
    },

    fetchTransactions (context, { contractId, page, filters = {}, cached = true }) {
      return contracts.fetchTransactions(contractId, page, filters)
        .then(data => context.commit('fetchTransactions', {
          contractId,
          filters,
          data,
          replace: !context.state.transactions[contractId] || !isEqual(context.state.transactions[contractId].filters, filters)
        }))
    },

    fetchTransactionCategories (context, cached = true) {
      if (cached && context.state.transactionCategories.length) {
        return Promise.resolve()
      }
      return contracts.fetchTransactionCategories()
        .then(data => context.commit('fetchTransactionCategories', data.data))
    },

    fetchTransactionsMovements (context, { contractId, cached = true }) {
      if (cached && context.state.transactionsMovements[contractId]) {
        return Promise.resolve()
      }
      return contracts.fetchTransactionsMovements(contractId)
        .then(data => context.commit('fetchTransactionsMovements', { data, contractId }))
    },

    updateInfos (context, data) {
      const infos = data.infos
      const id = data.id

      return contracts.updateInfos(id, infos)
        .then(() => contracts.fetchById(data.id)
          .then(data => {
            context.dispatch('accounts/fetchById', data.accountId, {root: true})
            context.commit('add', data)
          })
        )
    },

    updateName (context, data) {
      const { name, id } = data
      return contracts.update(id, { name })
        .then(data => {
          context.dispatch('accounts/fetchById', data.accountId, {root: true})
          context.commit('update', data)
        })
    },
    updateRisk (context, data) {
      const { riskSurvey, id } = data
      return contracts.update(id, { riskSurvey })
        .then(data => {
          context.dispatch('accounts/fetchById', data.accountId, {root: true})
          context.commit('update', data)
        })
    },

    updateStatus (context, data) {
      const status = data.contract.status
      const id = data.id
      return contracts.updateStatus(id, status)
        .then(() => {
          context.dispatch('accounts/fetchRepartition', false, {root: true})
          context.dispatch('user/fetch', false, {root: true})
            .then(() => contracts.fetchRepartition()
              .then(({ data }) => {
                context.commit('fetchRepartition', data)
              })
            )
        })
    },

    addRealEstate (context, data) {
      return contracts.addRealEstate(data)
        .then(({realEstate}) => {
          return contracts.estimateRealEstate(realEstate.id)
            .then(({estimate}) => {
              realEstate.estimate = estimate
              context.commit('addRealEstate', realEstate)
              return realEstate
            })
        })
    },

    fetchRealEstates (context, cached = true) {
      const { realEstates } = context.state
      if (cached && realEstates.length) {
        return Promise.resolve(realEstates)
      }

      return contracts.fetchRealEstate()
        .then(({realEstates}) => {
          realEstates.forEach(realEstate => {
            contracts.estimateRealEstate(realEstate.id)
              .then(({estimate}) => {
                realEstate.estimate = estimate
                context.commit('addRealEstate', realEstate)
              })
          })
        })
    },

    fetchRealEstateById (context, { id, cached = true }) {
      let realEstate
      if (cached && (realEstate = context.getters.getRealEstateById(id))) {
        return Promise.resolve(realEstate)
      }
      return contracts.fetchRealEstateById(id)
        .then(({realEstate}) => {
          return contracts.estimateRealEstate(id)
            .then(({estimate}) => {
              realEstate.estimate = estimate
              context.commit('addRealEstate', realEstate)
              return realEstate
            })
        })
    },

    updateRealEstate (context, data) {
      const infos = data.infos
      const id = data.id

      return realestates.updateRealEstate(id, infos)
        .then(() => context.dispatch('fetchRealEstateById', {id}))
    },

    deleteRealEstate (context, id) {
      return contracts.deleteRealEstate(id)
        .then(() => {
          context.commit('deleteRealEstate', id)
          context.dispatch('fetchRealEstates', false)
        })
    },

    fetchHistoryAll (context) {
      return contracts.fetchHistoryAll()
        .then(data => context.commit('saveGraphData', {...data, id: 'historyAll'}))
    },

    fetchHistoryWealth (context, data) {
      return contracts.fetchHistoryAll()
        .then(data => context.commit('saveGraphData', {...data, id: 'historyWealth'}))
    }
  },

  mutations: {
    analysing (state) {
      state.analysing = true
    },

    fetch (state, data) {
      state.list = data
    },

    add (state, data) {
      const index = findIndex(state.list, { id: data.id })
      if (state.optimizedPortfolio[data.id]) {
        state.optimizedPortfolio = {
          ...state.optimizedPortfolio,
          [data.id]: null
        }
      }
      if (state.analyses[data.id]) {
        state.analyses = {
          ...state.analyses,
          [data.id]: null
        }
      }
      if (index >= 0) {
        state.list = [
          ...state.list.slice(0, index),
          data,
          ...state.list.slice(index + 1)
        ]
      } else {
        state.list = [
          ...state.list,
          data
        ]
      }
    },

    addRealEstate (state, data) {
      const index = findIndex(state.realEstates, { id: data.id })
      if (index >= 0) {
        state.realEstates = [
          ...state.realEstates.slice(0, index),
          data,
          ...state.realEstates.slice(index + 1)
        ]
      } else {
        state.realEstates = [
          ...state.realEstates,
          data
        ]
      }
    },

    deleteRealEstate ({ realEstates }, id) {
      deleteRecord(realEstates, id)
    },

    fetchCompleteHistory (state, { data, subset }) {
      state.completeHistory = {
        ...state.completeHistory,
        [subset]: data
      }
    },

    fetchOptimize (state, { contractId, data }) {
      state.optimizedPortfolio = {
        ...state.optimizedPortfolio,
        [contractId]: data
      }
      state.analysing = false
    },

    fetchArbitration (state, { contractId, method, data }) {
      state.arbitrationPortfolio = {
        ...state.arbitrationPortfolio,
        [contractId]: {
          ...(state.arbitrationPortfolio[contractId] || {}),
          [method]: data
        }
      }
    },

    resetArbitration (state, contractId) {
      state.arbitrationPortfolio = {
        ...state.arbitrationPortfolio,
        [contractId]: {}
      }
    },

    fetchQuestions (state, data) {
      state.questions = data
    },

    fetchHistory (state, { contractId, history }) {
      state.histories = {
        ...state.histories,
        [contractId]: history
      }
    },

    fetchSlice (state, data) {
      data.forEach(data => updateRecord(state.list, data))
      state.list = [...state.list]
    },

    fetchTargets (state, data) {
      state.targets = data
    },

    fetchAnalyses (state, { contractId, section, data }) {
      let contractAnalyses = state.analyses[contractId]
      if (!contractAnalyses) {
        contractAnalyses = state.analyses[contractId] = {}
      }

      contractAnalyses[section] = data
      state.analyses = Object.assign({}, state.analyses)
      state.analysing = false
    },

    fetchRepartition (state, data) {
      state.contractsRepartition = data
    },

    saveAgreggate (state, data) {
      Vue.set(state.aggregates, data.id, data)
    },

    saveGraphData (state, data) {
      Vue.set(state.graphs, data.id, data)
    },

    fetchTransactions (state, { data, contractId, replace, filters }) {
      state.transactions = {
        ...state.transactions,
        [contractId]: (replace || !state.transactions[contractId]) ? {
          ...data,
          filters
        } : {
          ...data,
          filters,
          transactions: [
            ...state.transactions[contractId].transactions,
            ...data.transactions
              .filter(transaction => !state.transactions[contractId].transactions.find(item => item.id === transaction.id))
          ]
        }
      }
    },

    fetchTransactionCategories (state, data) {
      state.transactionCategories = data
    },

    fetchTransactionsMovements (state, { data, contractId }) {
      state.transactionsMovements = {
        ...state.transactionsMovements,
        [contractId]: data
      }
    },

    fetchComposition (state, { data, subset, type }) {
      state.composition = {
        ...state.composition,
        [subset]: {
          ...(state.composition[subset] || {}),
          [type]: data
        }
      }
    },

    update (state, data) {
      if (state.optimizedPortfolio[data.id]) {
        state.optimizedPortfolio = {
          ...state.optimizedPortfolio,
          [data.id]: null
        }
      }
      if (state.analyses[data.id]) {
        state.analyses = {
          ...state.analyses,
          [data.id]: null
        }
      }
      updateRecord(state.list, data)
    },

    reset (state) {
      const baseState = getBaseState()
      Object.keys(baseState).forEach(key => {
        state[key] = baseState[key]
      })
    }
  },

  getters: {
    getByAccount: ({ list }) => (id) => {
      return list.filter(contract => contract.accountId === id)
    },

    getById: ({ list }) => (id) => {
      return list.find(contract => contract.id === id)
    },

    getRealEstateById: ({ realEstates }) => (id) => {
      return realEstates.find(realEstate => realEstate.id === id)
    },

    getHistoryById: ({ histories }) => (id) => histories[id],

    getTargetByRisk: ({ targets }) => (risk) => {
      return targets[risk]
    },

    getAnalyseScores: ({ analyses }) => (id) => {
      const scores = {}
      analyses[id] && Object.keys(analyses[id]).forEach(key => { scores[key] = analyses[id][key].points })
      return scores
    },

    getFirstDate: ({ list }) => (id) => {
      const { history } = list.find(contract => contract.id === id)
      return history[0] ? history[0][0] : Date.now()
    },

    getTransactions: ({ transactions }) => (id) => {
      return transactions[id]
    },

    getTransactionsMovements: ({ transactionsMovements }) => (id) => {
      return transactionsMovements[id]
    },

    getOptimizedPortfolio: ({ optimizedPortfolio }) => (id) => optimizedPortfolio[id],

    getArbitrationPortfolio: ({ arbitrationPortfolio }) => (id, method) => arbitrationPortfolio[id] && arbitrationPortfolio[id][method],

    includesLifeInsurance: ({ list }) => list.some(contract => isLifeInsurance(contract)),

    isSettingsComplete: ({list}) => (id, isReco) => {
      const contract = list.find(contract => contract.id === id)
      const { infos, recommendedRisk } = contract
      const goal = Object.keys(infos).filter(key => !/(fee|fees|updatedat|contractId)$/gi.test(key))
        .reduce((goal, key) => {
          goal[key] = infos[key]
          return goal
        }, {})
      const fees = Object.keys(infos).filter(key => /(fee|fees)$/gi.test(key))
        .reduce((fee, key) => {
          if (isLifeInsurance(contract) ? feesList.includes(key) : feesSpecialList.includes(key)) {
            fee[key] = infos[key]
            return fee
          }
          return fee
        }, {})
      const info = {
        goal: Object.values(goal).every(field => Boolean(field) || field === 0),
        fees: Object.values(fees).every(field => Boolean(field) || field === 0),
        questions: Boolean(recommendedRisk)
      }
      if (isReco) {
        info.contractId = !!contract.infos.contractId
      }
      return info
    }
  }
})
